import {LayoutType} from '@wandb/weave/common/types/media';
import {fuzzyMatchRegex} from '@wandb/weave/common/util/fuzzyMatch';
import {ButtonSize, ButtonSizes} from '@wandb/weave/components/Button/types';
import {
  TextField,
  TextFieldSizes,
} from '@wandb/weave/components/Form/TextField';
import {IconName, IconNames} from '@wandb/weave/components/Icon/types';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import {
  ToggleButtonGroup,
  ToggleOption,
} from '@wandb/weave/components/ToggleButtonGroup';
import React, {useCallback, useMemo, useState} from 'react';
// eslint-disable-next-line wandb/no-deprecated-imports
import {twMerge} from 'tailwind-merge';

import {ClassLabelMap, ClassLabelNode} from '../../util/media';
import {AllMaskControls, MaskControl} from '../MediaCard';
import {Mask} from './Mask';

const BUTTON_VALUE_TO_LAYOUT: {[key: string]: LayoutType} = {
  Overlay: 'ALL_STACKED',
  Split: 'MASKS_NEXT_TO_IMAGE',
  Spread: 'ALL_SPLIT',
};
const BUTTON_VALUE_TO_ICON: {[key: string]: IconName} = {
  Overlay: IconNames.VersionsLayers,
  Split: IconNames.Split,
  Spread: IconNames.LayoutGrid,
};
const LAYOUT_TOGGLE_OPTIONS: ToggleOption[] = Object.entries(
  BUTTON_VALUE_TO_ICON
).map(([key, icon]) => ({
  value: key,
  icon,
}));

export const MaskControlsLayout = ({
  mediaKey,
  maskLayout,
  onChangeMaskLayout,
  size = ButtonSizes.Small,
}: {
  mediaKey: string;
  maskLayout: LayoutType;
  onChangeMaskLayout: (mediaKey: string, layoutType: LayoutType) => void;
  size?: ButtonSize;
}) => {
  const selectedLayout =
    Object.entries(BUTTON_VALUE_TO_LAYOUT).find(
      ([_, value]) => value === maskLayout
    )?.[0] ?? 'Overlay';

  const onToggleSelection = useCallback(
    (value: string) => {
      const layoutType = BUTTON_VALUE_TO_LAYOUT[value];
      if (layoutType) {
        onChangeMaskLayout(mediaKey, layoutType as LayoutType);
      }
    },
    [mediaKey, onChangeMaskLayout]
  );

  return (
    <ToggleButtonGroup
      className="rounded-lg"
      options={LAYOUT_TOGGLE_OPTIONS}
      value={selectedLayout}
      size={size}
      onValueChange={onToggleSelection}
    />
  );
};

const MaskControlsRow = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  return (
    <div
      className={twMerge(
        'flex w-full flex-row justify-between',
        'gap-10  py-4',
        className
      )}>
      {children}
    </div>
  );
};

type MaskControlsProps = {
  allMasks: ClassLabelMap;
  isImageShownForMask: (mediaKey: string, maskKey: string) => boolean;
  maskConfig: AllMaskControls;
  getMaskLayout: (mediaKey: string) => LayoutType;
  toggleImageForMask: (mediaKey: string, maskKey: string) => void;
  onChangeMaskLayout: (mediaKey: string, layoutType: LayoutType) => void;
  update: (newControl: Partial<MaskControl>, configPath: Array<string>) => void;
  toggleAllMasksUnder: (
    mediaKey: string,
    maskKey: string,
    classID?: string,
    relevantMasks?: {[key: string]: ClassLabelNode}
  ) => void;
  getMaskControl: (configPath: Array<string>) => MaskControl | undefined;
};

export const MaskControls = (props: MaskControlsProps) => {
  const {
    allMasks,
    isImageShownForMask,
    getMaskControl,
    getMaskLayout,
    toggleImageForMask,
    update,
    toggleAllMasksUnder,
    onChangeMaskLayout,
  } = props;

  const [maskSearchQuery, setMaskSearchQuery] = useState('');
  const maskQueryRegex = useMemo(() => {
    return fuzzyMatchRegex(maskSearchQuery);
  }, [maskSearchQuery]);

  return (
    <Tailwind
      data-test="mask-controls"
      key="mask-control"
      className="flex-none grow">
      {Object.keys(allMasks).map(mediaKey => {
        const relevantMasks = allMasks[mediaKey];
        const maskLayout = getMaskLayout(mediaKey);
        return (
          <React.Fragment key={mediaKey}>
            <MaskControlsRow className={twMerge('pt-16')}>
              <div className="flex-1 rounded-md border border-solid border-moon-250">
                <TextField
                  icon={IconNames.Search}
                  variant="ghost"
                  size={TextFieldSizes.Medium}
                  value={maskSearchQuery}
                  placeholder="Search"
                  onChange={setMaskSearchQuery}
                />
              </div>
              <div className="flex-none">
                <MaskControlsLayout
                  mediaKey={mediaKey}
                  maskLayout={maskLayout}
                  onChangeMaskLayout={onChangeMaskLayout}
                />
              </div>
            </MaskControlsRow>

            <MaskControlsRow>
              <Mask
                getMaskControl={getMaskControl}
                title="All Masks"
                maskKey="all"
                mediaKey={mediaKey}
                maskQueryRegex={maskQueryRegex}
                update={update}
                canShowImage={false}
                toggleAllMasksUnder={toggleAllMasksUnder}
              />
            </MaskControlsRow>
            {Object.keys(relevantMasks).map(maskKey => {
              const classDict = allMasks[mediaKey][maskKey].value;
              return (
                <React.Fragment key={maskKey}>
                  <MaskControlsRow>
                    <Mask
                      getMaskControl={getMaskControl}
                      title={maskKey}
                      classDict={classDict}
                      isImageShownForMask={isImageShownForMask}
                      maskKey={maskKey}
                      maskQueryRegex={maskQueryRegex}
                      mediaKey={mediaKey}
                      toggleImageForMask={toggleImageForMask}
                      update={update}
                      toggleAllMasksUnder={toggleAllMasksUnder}
                    />
                  </MaskControlsRow>
                </React.Fragment>
              );
            })}
          </React.Fragment>
        );
      })}
    </Tailwind>
  );
};
